import type { WellnessScores } from '@qi/shared';
import { ScoreRing } from '@qi/ui';
import { createFileRoute } from '@tanstack/react-router';
import { FlowActionBar } from '../components/shell/FlowActionBar.tsx';
import { GuestSavePrompt } from '../components/shell/GuestSavePrompt.tsx';
import { useSessionStore } from '../stores/session-store.ts';

// Reached after a rebalance session ends (route path kept as /compare to avoid
// churn). It is NOT a before/after comparison: a single session can't be
// measured against a fresh capture, so we show the real scan the user started
// from and invite them to rescan later to see how their state has actually
// shifted — no fabricated "after" numbers.
export const Route = createFileRoute('/_app/compare')({
  component: SessionCompletePage,
});

// Overall score = mean of the five wellness metrics, matching the results page
// composite. Calm inverts stressLoad so higher always reads better.
function composite(s: WellnessScores): number {
  const parts = [s.focus, s.energy, s.recovery, s.emotionalCoherence, 100 - s.stressLoad];
  return Math.round(parts.reduce((sum, v) => sum + v, 0) / parts.length);
}

function SessionCompletePage() {
  const lastScan = useSessionStore((s) => s.lastScan);
  const pre = lastScan?.scores ?? null;

  return (
    <>
      <FlowActionBar
        back={{ to: '/dashboard', label: 'Back to dashboard' }}
        primary={{ to: '/scan/bioscan', label: 'Scan again' }}
      />
      <div className="mx-auto max-w-2xl space-y-10 px-6 pb-28 pt-6">
        <header className="space-y-2 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.32em] text-ink-2">
            Session complete
          </p>
          <h1 className="font-display text-4xl text-ink-0">Nice work — that's your session in.</h1>
          <p className="mx-auto max-w-md text-pretty text-ink-1">
            Your body keeps responding after the tones stop. Come back and rescan in a day or two to
            see how your resonance has actually shifted.
          </p>
        </header>

        {pre && (
          <section className="space-y-4 rounded-lg border border-hairline bg-surface-1 p-6">
            <p className="text-center font-mono text-xs uppercase tracking-[0.18em] text-ink-2">
              Where you started
            </p>
            <div className="flex items-center justify-center">
              <ScoreRing score={composite(pre)} />
            </div>
            <p className="text-center text-sm text-ink-2">
              This is your last scan — your baseline to measure the next one against.
            </p>
          </section>
        )}

        {/* Conversion moment — capture the email so we can remind them to come
            back and rescan. Collapses to a quiet confirmation once signed in. */}
        <GuestSavePrompt
          headline="Get your rescan reminder"
          description="Sign in to save today's session and we'll nudge you when it's time to scan again and watch your trend build."
          cta="Save and remind me"
          savedHeadline="Saved to your timeline"
          savedDescription="Your session is on the dashboard. We'll remind you when it's time to rescan."
        />
      </div>
    </>
  );
}
